/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { useMemo } from 'react'
import {
  deleteLeaseFile,
  downloadLeaseFileById,
  findManyLeaseFiles,
  uploadLeaseFile
} from '../../lib/rest/leaseFileAPIClient'
import FileDropZone from '../file/FileDropZone'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { FormattedMessage } from 'react-intl'
import { Card, Text } from '@mantine/core'
import { useSettingsContext } from '../SettingsProvider'

export type LeaseFileInputProps = {
  disabled?: boolean,
  documentType: LeaseDocumentType,
  label: string,
  leaseId?: number,
  required?: boolean,
  tmpRef: string,
}

function LeaseFileInput (props: LeaseFileInputProps) {
  const {
    disabled,
    documentType,
    label,
    leaseId,
    required,
    tmpRef
  } = props

  const { settings } = useSettingsContext()

  const filesQuery = useQuery({
    enabled: true,
    queryKey: ['leaseFiles', documentType, leaseId, tmpRef],
    queryFn: () => findManyLeaseFiles({ leaseId, tmpRef, documentType }),
    refetchOnWindowFocus: false
  })

  const files = useMemo(() => (
    (filesQuery.data != null
      ? getHalEmbedded(filesQuery.data)?.files
      : null) || []
  ), [filesQuery.data])

  const file = useMemo(() => files[0], [files])

  const downloader = useQuery({
    enabled: false,
    queryKey: ['downloadLeaseFile', file],
    queryFn: () => downloadLeaseFileById(file?.id)
  })

  const deleter = useMutation({
    mutationKey: ['deleteLeaseFile', file],
    mutationFn: () => deleteLeaseFile(file?.id),
    onSuccess: () => filesQuery.refetch()
  })

  const uploader = useMutation({
    mutationKey: ['uploadLeaseFile', documentType, leaseId, tmpRef],
    mutationFn: (f: File) => uploadLeaseFile(f, tmpRef, leaseId, documentType),
    onSuccess: () => filesQuery.refetch()
  })

  const error =
    deleter.error ||
    uploader.error ||
    downloader.error

  return (
    <Card withBorder p="sm" shadow="none">
      <FileDropZone
        accept={settings.LEASE_FILE_TYPES}
        contentLength={file?.size}
        disabled={disabled}
        error={error}
        label={label}
        lastModified={file?.createdAt}
        loading={uploader.isPending || deleter.isPending}
        maxSize={settings.LEASE_MAX_UPLOAD_SIZE}
        onChange={uploader.mutate}
        onDelete={deleter.mutateAsync}
        // placeholder={label}
        url={`/api/lease-files/${file?.id}/file`}
        value={file?.name}
      >
        {required && file == null
          ? <Text c="orange" size="sm">
            <FormattedMessage
              id="file_is_missing"
              defaultMessage="Le fichier est manquant."
            />
          </Text>
          : null}
      </FileDropZone>
    </Card>
  )
}

export default LeaseFileInput
